import { create } from "zustand";
import { apiClient } from "@/api/client";

// ─── API Response Types ───────────────────────────────────────────────

export interface ComponentSettings {
  // Text settings
  input_type?: string;
  placeholder?: string;
  label?: string;
  rows?: number;
  character_limit?: number;
  text_defaults?: {
    color: string;
    font_size: number;
    font_weight: string;
  };
  show_on_canvas?: boolean;

  // Media settings
  slots?: number;
  accept?: string;
  max_size_mb?: number;

  // Button settings
  bg?: string;
  text_color?: string;
  border_color?: string;
  border_radius?: string;
  shadow?: string;

  // Shape settings
  shape_defaults?: {
    color: string;
    overlay: boolean;
    background: boolean;
  };

  // Attributes settings
  validation?: { type: string };
  options?: Array<{ title: string; value: string }>;
  visual_only?: boolean;
  color?: string;
  border_style?: string;
}

export interface SubComponent {
  id: string;
  name: string;
  display_name: string;
  /**
   * Stable form key. The API serialises this as
   * `canvas_component_sub_master_key`; it is normalised to `key` on load so
   * consumers have one name to use. Falls back to `name`, which is unique
   * within a master, if the API ever omits it.
   */
  key: string;
  component_category: "template" | "content" | "media" | "button" | "objective";
  sort_order: number;
  settings: ComponentSettings;
}

export interface CanvasComponent {
  id: string;
  name: string;
  display_name: string;
  sort_order: number;
  sub_components: SubComponent[];
}

export interface CanvasComponentsResponse {
  data: CanvasComponent[];
}

// ─── Canvas Save Types ────────────────────────────────────────────────

export type SaveStatus = "draft" | "in_progress" | "completed";

export type SaveDomain = "master" | "components" | "locations" | "adInfo" | "media";

export interface SaveComponentPayload {
  id?: string;
  canvas_component_master_id: string;
  canvas_component_sub_master_id: string;
  aggregated_json: Record<string, any>;
}

export interface SaveLocationPayload {
  id?: string;
  canvas_component_master_id: string;
  canvas_component_sub_master_id: string;
  canvas_component_info_id: string;
  interface_id: string;
  location_json: {
    x: number;
    y: number;
    width: number;
    height: number;
    z_index: number;
    rotation?: number;
    scale?: number;
  };
}

export interface SaveAdInfoPayload {
  id?: string;
  ad_info_blocks_master_id: string;
  ad_info_blocks_components_id: string;
  value_json: Record<string, any>;
}

// ─── Store Types ──────────────────────────────────────────────────────

interface AdBuilderState {
  // API data
  canvasComponents: CanvasComponent[];
  isLoadingComponents: boolean;
  componentsError: string | null;

  // Canvas save state
  canvasMasterId: string | null;
  isSaving: boolean;
  saveError: string | null;
  dirtyDomains: Set<SaveDomain>;

  // Loaded canvas data (from show endpoint)
  loadedCanvasData: any | null;
  isLoadingCanvas: boolean;

  // Actions
  fetchCanvasComponents: () => Promise<void>;

  // Canvas save actions
  loadCanvas: (adCategoryMappingId: string) => Promise<void>;
  saveMaster: (adCategoryMappingId: string, status: SaveStatus, canvasInfoAggregated?: Record<string, any>) => Promise<string>;
  saveComponents: (canvasMasterId: string, components: SaveComponentPayload[]) => Promise<any[]>;
  saveLocations: (canvasMasterId: string, locations: SaveLocationPayload[]) => Promise<any[]>;
  saveAdInfo: (canvasMasterId: string, adInfo: SaveAdInfoPayload[]) => Promise<void>;
  saveMedia: (canvasMasterId: string, canvasComponentInfoId: string, file: File, slotIndex: number) => Promise<void>;
  markDirty: (domain: SaveDomain) => void;
  clearDirty: (domain: SaveDomain) => void;
  setCanvasMasterId: (id: string | null) => void;
}

// ─── Store ────────────────────────────────────────────────────────────

const useAdBuilderStore = create<AdBuilderState>((set, get) => ({
  canvasComponents: [],
  isLoadingComponents: false,
  componentsError: null,

  canvasMasterId: null,
  isSaving: false,
  saveError: null,
  dirtyDomains: new Set(),

  loadedCanvasData: null,
  isLoadingCanvas: false,

  fetchCanvasComponents: async () => {
    set({ isLoadingComponents: true, componentsError: null });
    try {
      const response = await apiClient.get<any>(
        "/canvas-components/hierarchy",
      );
      // The controller already emits `sub_components` / `key` / `settings`.
      // The fallbacks below only guard against the raw-model shape
      // (`sub_masters` / `canvas_component_sub_master_key` / `settings_json`)
      // in case that transformation is ever bypassed.
      const normalized: CanvasComponent[] = (response.data.data ?? []).map(
        (master: any) => ({
          ...master,
          sub_components: (master.sub_components ?? master.sub_masters ?? []).map(
            (sub: any) => ({
              ...sub,
              key: sub.key ?? sub.canvas_component_sub_master_key ?? sub.name,
              settings: sub.settings ?? sub.settings_json ?? {},
            }),
          ),
        }),
      );
      set({
        canvasComponents: normalized,
        isLoadingComponents: false,
      });
    } catch (error: any) {
      set({
        isLoadingComponents: false,
        componentsError:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch canvas components",
      });
    }
  },

  loadCanvas: async (adCategoryMappingId: string) => {
    set({ isLoadingCanvas: true });
    try {
      const response = await apiClient.get(`/canvas/${adCategoryMappingId}/show`);
      const data = response.data?.data;
      set({
        loadedCanvasData: data,
        canvasMasterId: data?.canvas_master?.id ?? null,
        isLoadingCanvas: false,
      });
    } catch (error: any) {
      set({ isLoadingCanvas: false });
      throw new Error(
        error?.response?.data?.message || error?.message || "Failed to load canvas",
      );
    }
  },

  saveMaster: async (adCategoryMappingId: string, status: SaveStatus, canvasInfoAggregated?: Record<string, any>) => {
    set({ isSaving: true, saveError: null });
    try {
      const payload: Record<string, any> = { status };
      if (canvasInfoAggregated) {
        payload.canvas_info_aggregated = JSON.stringify(canvasInfoAggregated);
      }
      const response = await apiClient.post(
        `/canvas/${adCategoryMappingId}/master`,
        payload,
      );
      const masterId: string = response.data.data.id;
      set({ canvasMasterId: masterId, isSaving: false });
      get().clearDirty("master");
      return masterId;
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to save canvas master";
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  saveComponents: async (canvasMasterId: string, components: SaveComponentPayload[]) => {
    set({ isSaving: true, saveError: null });
    try {
      const response = await apiClient.post(
        `/canvas/${canvasMasterId}/components`,
        { components },
      );
      set({ isSaving: false });
      get().clearDirty("components");
      return response.data.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to save components";
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  saveLocations: async (canvasMasterId: string, locations: SaveLocationPayload[]) => {
    set({ isSaving: true, saveError: null });
    try {
      const response = await apiClient.post(`/canvas/${canvasMasterId}/locations`, { locations });
      set({ isSaving: false });
      get().clearDirty("locations");
      return response.data.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to save locations";
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  saveAdInfo: async (canvasMasterId: string, adInfo: SaveAdInfoPayload[]) => {
    set({ isSaving: true, saveError: null });
    try {
      await apiClient.post(`/canvas/${canvasMasterId}/ad-info`, { ad_info: adInfo });
      set({ isSaving: false });
      get().clearDirty("adInfo");
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to save ad info";
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  saveMedia: async (canvasMasterId: string, canvasComponentInfoId: string, file: File, slotIndex: number) => {
    set({ isSaving: true, saveError: null });
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("canvas_component_info_id", canvasComponentInfoId);
      formData.append("slot_index", String(slotIndex));
      await apiClient.post(`/canvas/${canvasMasterId}/media`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set({ isSaving: false });
      get().clearDirty("media");
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "Failed to upload media";
      set({ isSaving: false, saveError: message });
      throw new Error(message);
    }
  },

  markDirty: (domain: SaveDomain) => {
    set((state) => {
      const next = new Set(Array.from(state.dirtyDomains));
      next.add(domain);
      return { dirtyDomains: next };
    });
  },

  clearDirty: (domain: SaveDomain) => {
    set((state) => {
      const next = new Set(state.dirtyDomains);
      next.delete(domain);
      return { dirtyDomains: next };
    });
  },

  setCanvasMasterId: (id: string | null) => {
    set({ canvasMasterId: id });
  },
}));

export default useAdBuilderStore;
